import { EmailEntityId } from "@sp-crm/core";
import {
    AdvancedSearchEntityType,
    ContactSearchHitFragmentFragment,
    ContactSearchHitType,
    ConversationThreadType,
} from "../../generated/graphql";

export type MessageChannelFilter = "all" | "email" | "sms" | "voice";

export const threadMatchesChannelFilter = (
    threadType: ConversationThreadType,
    filter: MessageChannelFilter,
): boolean => {
    switch (filter) {
        case "all":
            return true;
        case "email":
            return threadType === ConversationThreadType.EmailThread;
        case "sms":
            return threadType === ConversationThreadType.Sms;
        case "voice":
            return threadType === ConversationThreadType.VoiceCall;
        default: {
            const exhaustiveCheck: never = filter;
            throw new RangeError(
                `Expected ${exhaustiveCheck} to be matched, but wasn't.`,
            );
        }
    }
};

const channelFilters: MessageChannelFilter[] = ["all", "email", "sms", "voice"];

export function getChannelFilterCounts(
    threads: ReadonlyArray<{ type: ConversationThreadType }>,
): Record<MessageChannelFilter, number> {
    const counts = {} as Record<MessageChannelFilter, number>;
    for (const filter of channelFilters) {
        counts[filter] = threads.filter(thread =>
            threadMatchesChannelFilter(thread.type, filter),
        ).length;
    }
    return counts;
}

export interface SentMessageForPreview {
    bodyPreview?: string | null;
    createdAt: string;
}

export interface ReceivedMessageForPreview {
    bodyPreview?: string | null;
    receivedTimestamp: string;
}

export function getEntityDisplayName(
    hit: ContactSearchHitFragmentFragment,
): string | null {
    switch (hit.type) {
        case ContactSearchHitType.Client:
            return hit.client?.name ?? null;
        case ContactSearchHitType.BestContact:
            return hit.contact?.name ?? hit.client?.name ?? null;
        case ContactSearchHitType.Community:
            return hit.community?.name ?? null;
        case ContactSearchHitType.ClientContact:
        case ContactSearchHitType.CommunityContact:
            return hit.contact?.name ?? null;
        case ContactSearchHitType.ReferenceContact:
            return hit.contact?.name ?? hit.referenceContact?.contactEntity?.name ?? null;
        case ContactSearchHitType.ReferenceBusiness:
            return hit.referenceBusiness?.businessContactEntity?.name ?? null;
        default: {
            const exhaustiveCheck: never = hit.type;
            throw new RangeError(
                `Expected ${exhaustiveCheck} to be matched, but wasn't.`,
            );
        }
    }
}

export interface EmailEntityRef {
    entityId: EmailEntityId;
    entityType: AdvancedSearchEntityType;
}

export function emailEntityRefFromHit(
    hit: ContactSearchHitFragmentFragment,
): EmailEntityRef | null {
    switch (hit.type) {
        case ContactSearchHitType.Client:
        case ContactSearchHitType.BestContact:
        case ContactSearchHitType.ClientContact: {
            const clientId = hit.client?.id ?? hit.clientContact?.clientId ?? null;
            return clientId
                ? { entityId: clientId, entityType: AdvancedSearchEntityType.Client }
                : null;
        }
        case ContactSearchHitType.Community:
        case ContactSearchHitType.CommunityContact: {
            const communityId =
                hit.community?.id ?? hit.communityContact?.communityId ?? null;
            return communityId
                ? {
                      entityId: communityId,
                      entityType: AdvancedSearchEntityType.Community,
                  }
                : null;
        }
        case ContactSearchHitType.ReferenceContact:
            return hit.referenceContact
                ? {
                      entityId: hit.referenceContact.id,
                      entityType: AdvancedSearchEntityType.ReferenceContact,
                  }
                : null;
        case ContactSearchHitType.ReferenceBusiness:
            return hit.referenceBusiness
                ? {
                      entityId: hit.referenceBusiness.id,
                      entityType: AdvancedSearchEntityType.ReferenceBusiness,
                  }
                : null;
        default: {
            const exhaustiveCheck: never = hit.type;
            throw new RangeError(
                `Expected ${exhaustiveCheck} to be matched, but wasn't.`,
            );
        }
    }
}

export const pickEmailThreadPreview = (
    sentMessages: SentMessageForPreview[],
    mailboxMessages: ReceivedMessageForPreview[],
): string => {
    const newestSent = sentMessages[0];
    const newestReceived = mailboxMessages[0];
    if (!newestSent) {
        return newestReceived?.bodyPreview ?? "";
    }
    if (!newestReceived) {
        return newestSent.bodyPreview ?? "";
    }
    const receivedIsNewer =
        new Date(newestReceived.receivedTimestamp) > new Date(newestSent.createdAt);
    return (receivedIsNewer ? newestReceived : newestSent).bodyPreview ?? "";
};
